import { Alert, Button, Input, MonetaryAmount, Prompt, TextArea, TextTooltip } from '@components'
import { ContactSelector } from '@components/uas/ContactSelector'
import {
  InvoiceRecurrenceMode,
  InvoiceRepetitionOptions,
  ReferenceMethods,
  VatCode,
  getContactPageUrl,
  getInvoicingPageUrl,
  getRuleFieldsForRecurrencePreset,
  recurrenceModeFromFrequency,
  recurrenceScheduleFromRule
} from '@constants'
import { useBusinessContext } from '@context'
import { readLocalStorageValue, useCacheFormValues, useLocalStorage } from '@hooks'
import {
  ContactType,
  createInvoice,
  fetchContact,
  IInvoice,
  IInvoiceFormValues,
  IInvoiceRow,
  IProduct,
  invalidateDashboardQueries,
  updateInvoice
} from '@query'
import { invoiceFormCacheKey, setAPIErrors } from '@utils'
import dayjs from 'dayjs'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Controller, FormProvider, get, useForm, useWatch } from 'react-hook-form'
import { Trans, useTranslation } from 'react-i18next'
import { FaQuestionCircle, FaSave, FaUndo } from 'react-icons/fa'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useHistory } from 'react-router-dom'
import styled from 'styled-components'
import { InvoiceRows } from './InvoiceRows'
import { InvoiceCopyDropdown } from './InvoiceCopyDropdown'
import { InvoiceFormMoreSection } from './InvoiceFormMoreSection'
import InvoiceFormRowToggle from './InvoiceFormRowToggleButton'
import { AttachmentSelector } from '@containers/AttachmentSelector'
import { TagSelector } from '@root/containers/TagSelector/TagSelector'
import { InvoiceFormRecurrenceSection } from './InvoiceFormRecurrenceSection'
import { isGermanBusinessCountry, isValidDeBuyerReferenceFormat } from '../xrechnungDelivery'
import { defaultSettlementDate } from './defaultSettlementDate'

interface IAdvancedRows extends Partial<IInvoiceRow>, Partial<IProduct> {}

export const calculateInvoiceRowValues = (
  vat_rate: number,
  amount: number,
  product_count: number
) => {
  const vatExclusiveAmount = amount ? amount : 0

  const vatRate = vat_rate ? vat_rate : 0

  const net = vatExclusiveAmount * product_count
  const vat = (net * vatRate) / 100
  const tot = net + vat

  return {
    net,
    vat,
    tot
  }
}

export const DEFAULT_DATA_INVOICE_ROW: Partial<IInvoiceRow> = {
  vat_code: VatCode.TAX_FREE,
  vat_rate: 0,
  name: '',
  product_count: null,
  amount: 0,
  description: '',
  product: null,
  unit: ''
}

const useInvoiceTotalValues = (invoiceRows: IAdvancedRows[], isCreditNote: boolean) => {
  const readyRows = (invoiceRows || []).filter(row => {
    const hasProductCount = Boolean(row.product_count)
    return hasProductCount
  })

  const rowAmounts = readyRows.map(({ vat_rate, amount, product_count }) => {
    const { net, vat, tot } = calculateInvoiceRowValues(vat_rate, amount, product_count)

    return {
      net,
      vat,
      tot
    }
  })
  const totals = rowAmounts.reduce(
    (prev, curr) => ({
      net: prev.net + curr.net,
      vat: prev.vat + curr.vat,
      tot: prev.tot + curr.tot
    }),
    { net: 0, vat: 0, tot: 0 }
  )

  const _round = (value: number): number => Math.round(value * 100) / 100

  return {
    totals: {
      net: _round(totals.net) * (isCreditNote ? -1 : 1),
      vat: _round(totals.vat) * (isCreditNote ? -1 : 1),
      tot: _round(totals.tot) * (isCreditNote ? -1 : 1)
    }
  }
}

interface Props {
  editInvoiceId?: number
  defaultValues?: Partial<IInvoice>
  isCreditNoteFor?: number
  onSubmit: (created?: IInvoice) => void
}

const defaultPostDueFlag = (
  isCreditNote: boolean,
  invoiceValue: boolean | undefined,
  businessDefault: boolean | undefined
): boolean => {
  if (isCreditNote) {
    return false
  }
  return invoiceValue !== undefined ? invoiceValue : !!businessDefault
}

export const InvoiceForm: React.FC<Props> = ({
  editInvoiceId,
  defaultValues = {},
  isCreditNoteFor = null,
  onSubmit: _onSubmit
}) => {
  const {
    slug,
    data: {
      country: businessCountry,
      can_invoice: canInvoice,
      has_tags: hasTags,
      has_accrual_based_entries_for_invoicing: hasAccuralBasedEntries,
      is_automatic_debt_collection_enabled: isAutomaticDebtCollectionEnabled,
      soft_reminder_steps: softReminderSteps,
      invoicing_default_email_subject: invoicingDefaultEmailSubject,
      invoicing_default_email_content: invoicingDefaultEmailContent,
      invoicing_default_settlement_date: invoicingDefaultSettlementDate
    }
  } = useBusinessContext()

  const [advancedMode, setAdvancedMode] = useLocalStorage<boolean>(
    `${slug}.invoicing.advancedInvoiceRowsEnabled`,
    false
  )
  const [t] = useTranslation()
  const queryClient = useQueryClient()
  const history = useHistory()
  const isEdit = !!editInvoiceId
  const _isCreditNoteFor = isCreditNoteFor || defaultValues.is_credit_note_for

  const setAdvancedModeCallback = useCallback(
    (value: boolean) => setAdvancedMode(value),
    [setAdvancedMode]
  )

  const getDefaultInvoicingDate = useCallback(
    (data: Partial<IInvoice>) => {
      return data?.invoicing_date || dayjs().format('YYYY-MM-DD')
    },
    [defaultValues]
  )

  const isGermanBusiness = isGermanBusinessCountry(businessCountry)

  const getDefaultValues = useCallback(
    (data: Partial<IInvoice>): IInvoiceFormValues => {
      const sourceRows = (data.rows || [DEFAULT_DATA_INVOICE_ROW]) as IInvoiceRow[]
      const rows = isEdit
        ? sourceRows
        : sourceRows.map(row => {
            const next = { ...row }
            delete next.id
            return next
          })

      return {
        invoicing_date: getDefaultInvoicingDate(data),
        settlement_date:
          hasAccuralBasedEntries === true
            ? data?.settlement_date ||
              defaultSettlementDate(getDefaultInvoicingDate(data), invoicingDefaultSettlementDate)
            : null,
        receiver: data.receiver || null,
        payment_condition_days: data.payment_condition_days || 14,
        description: data.description,
        reference: data.reference,
        buyer_reference: data.buyer_reference,
        seller_reference: data.seller_reference,
        penalty_interest: data.penalty_interest || 7,
        is_credit_note_for: data.is_credit_note_for || _isCreditNoteFor,
        rows,
        is_active_recurrence_template: data?.is_active_recurrence_template || false,
        recurrence_rule: {
          start: data?.recurrence_rule?.start,
          rule: {
            frequency:
              data?.recurrence_rule?.rule?.frequency !== undefined
                ? data.recurrence_rule.rule.frequency
                : InvoiceRepetitionOptions.NO_SELECTION,
            interval: data?.recurrence_rule?.rule?.interval ?? 1,
            by_month_day: data?.recurrence_rule?.rule?.by_month_day,
            by_month: data?.recurrence_rule?.rule?.by_month
          }
        },
        recurrence_mode: recurrenceModeFromFrequency(data?.recurrence_rule?.rule?.frequency),
        recurrence_interval_preset: recurrenceScheduleFromRule(data?.recurrence_rule?.rule),
        recurrence_end: data?.recurrence_end || '',
        recurrence_parent_id: data?.recurrence_parent_id,
        recurrence_reference_method:
          data?.recurrence_reference_method !== undefined
            ? data?.recurrence_reference_method
            : ReferenceMethods.roll,
        recurrence_email_subject:
          data?.recurrence_email_subject ?? invoicingDefaultEmailSubject ?? undefined,
        recurrence_email_content:
          data?.recurrence_email_content ?? invoicingDefaultEmailContent ?? undefined,
        next_recurrences: data?.next_recurrences,
        attachments: data?.attachments,
        tag_ids: data?.tag_ids || [],
        delivery_method: data?.delivery_method,
        is_automatic_debt_collection_enabled: defaultPostDueFlag(
          !!(data.is_credit_note_for || _isCreditNoteFor),
          data?.is_automatic_debt_collection_enabled,
          isAutomaticDebtCollectionEnabled
        ),
        is_automatic_soft_reminder_enabled: defaultPostDueFlag(
          !!(data.is_credit_note_for || _isCreditNoteFor),
          data?.is_automatic_soft_reminder_enabled,
          (softReminderSteps?.length ?? 0) > 0
        )
      }
    },
    [
      getDefaultInvoicingDate,
      hasAccuralBasedEntries,
      invoicingDefaultSettlementDate,
      invoicingDefaultEmailContent,
      invoicingDefaultEmailSubject,
      isAutomaticDebtCollectionEnabled,
      softReminderSteps,
      isEdit,
      _isCreditNoteFor
    ]
  )

  const initialValues = getDefaultValues(defaultValues)
  const restoredDraft =
    !isEdit && !_isCreditNoteFor
      ? readLocalStorageValue<Partial<IInvoice>>(invoiceFormCacheKey(slug))
      : undefined
  const settlementDateBaseline = isEdit ? initialValues : restoredDraft
  const settlementDateEdited = useRef(
    !!settlementDateBaseline?.invoicing_date &&
      !!settlementDateBaseline.settlement_date &&
      settlementDateBaseline.settlement_date !==
        defaultSettlementDate(settlementDateBaseline.invoicing_date, invoicingDefaultSettlementDate)
  )
  const methods = useForm<IInvoiceFormValues>({
    defaultValues: initialValues
  })
  const {
    register,
    control,
    handleSubmit,
    setError,
    reset,
    getValues,
    setValue,
    formState: { errors, isSubmitting }
  } = methods

  // PERSISTING
  // ==========

  const [receiver, invoicingDate, paymentCondition, invoiceRows, buyerReference] = useWatch({
    control,
    name: ['receiver', 'invoicing_date', 'payment_condition_days', 'rows', 'buyer_reference']
  })

  const isCreditNote = Boolean(_isCreditNoteFor)
  const [showCreditNoteConfirm, setShowCreditNoteConfirm] = useState(false)
  const pendingCreditNote = useRef<IInvoiceFormValues | null>(null)
  const { totals } = useInvoiceTotalValues(invoiceRows, isCreditNote)

  const { resetCache } = useCacheFormValues<Partial<IInvoice>>({
    cacheKey: invoiceFormCacheKey(slug),
    getValues,
    setValue,
    enabled: !isEdit && !isCreditNote
  })

  const { data: receiverData } = useQuery({
    queryKey: [slug, 'contact', receiver],
    queryFn: () => fetchContact({ slug, contactId: receiver }),
    ...{ enabled: !!receiver }
  })

  const receiverCantBeInvoiced = receiverData?.can_be_invoiced === false
  const isBusinessReceiver = !!receiver && receiverData?.type === ContactType.BUSINESS
  const showGermanBuyerReference = isGermanBusiness && isBusinessReceiver
  const contactBuyerReference = receiverData?.xrechnung_buyer_reference?.trim() || ''
  const showBuyerReferenceOverrideNote =
    Boolean(buyerReference?.trim()) && Boolean(contactBuyerReference)

  useEffect(() => {
    // Wait for receiverData so a loading gap between business contacts does not wipe BT-10.
    if (!isGermanBusiness || !receiver || !receiverData) return

    if (receiverData.type !== ContactType.BUSINESS && getValues('buyer_reference')) {
      setValue('buyer_reference', '')
    }
  }, [isGermanBusiness, receiver, receiverData, getValues, setValue])

  const dueDate = useMemo(() => {
    return dayjs(invoicingDate).add(paymentCondition, 'day').format('YYYY-MM-DD')
  }, [invoicingDate, paymentCondition])

  const resetForm = useCallback(() => {
    reset(undefined, { keepDefaultValues: true })
    resetCache()
    settlementDateEdited.current = false
  }, [])

  const options = {
    onError: error => {
      const { status, data } = (error as any) ?? {}
      if (status === 400) {
        setAPIErrors(data, setError)
      }
    },
    onSuccess: async data => {
      // Clear draft before navigate/close so unmount cannot re-save it.
      resetForm()

      queryClient.setQueryData([slug, 'invoices', data.id], data)

      const url = getInvoicingPageUrl(slug, { id: data.id, type: 'sales' })
      history.push(url)

      // Wait only for this invoice; do not await list refreshes (infinite scroll).
      await queryClient.invalidateQueries({
        queryKey: [slug, 'invoices', data.id],
        exact: true
      })
      void queryClient.invalidateQueries({ queryKey: [slug, 'combined_invoices'] })
      if (data.is_credit_note_for) {
        void queryClient.invalidateQueries({
          queryKey: [slug, 'invoices', data.is_credit_note_for],
          exact: true
        })
      }
      void invalidateDashboardQueries(queryClient, slug)
      if (data.pdf?.id) {
        void queryClient.invalidateQueries({ queryKey: [slug, 'files', data.pdf.id] })
      }
    }
  }

  const updateMutation = useMutation<IInvoice, unknown, IInvoiceFormValues>({
    mutationFn: data => updateInvoice({ slug, invoiceId: editInvoiceId }, data),
    ...options
  })
  const createMutation = useMutation<IInvoice, unknown, IInvoiceFormValues>({
    mutationFn: data => createInvoice({ slug }, data),
    ...options
  })

  const setRecurrenceRuleEdgeCases = (data: IInvoiceFormValues) => {
    // Needs to be set here
    data.is_active_recurrence_template = true

    // For now this is the default value from frontend
    data?.recurrence_rule && (data.recurrence_rule.start = null)

    const preset = data.recurrence_interval_preset

    if (preset && data.recurrence_rule?.rule) {
      const { frequency, interval } = getRuleFieldsForRecurrencePreset(
        preset,
        data.recurrence_rule.rule.interval
      )
      data.recurrence_rule.rule.frequency = frequency
      data.recurrence_rule.rule.interval = interval
    } else if (data?.recurrence_rule?.rule?.frequency === InvoiceRepetitionOptions.MONTHLY) {
      data.recurrence_rule.rule.interval = data.recurrence_rule.rule.interval ?? 1
    } else if (data?.recurrence_rule?.rule?.frequency === InvoiceRepetitionOptions.YEARLY) {
      data.recurrence_rule.rule.interval = 1
    }

    // If date picker has not been used, the recurrence will not have ending date
    data?.recurrence_end === '' && (data.recurrence_end = null)

    delete data.recurrence_mode
    delete data.recurrence_interval_preset
  }

  const clearRecurrenceFields = (data: IInvoiceFormValues) => {
    data.is_active_recurrence_template = false
    data.recurrence_rule = null
    data.recurrence_end = null
    data.recurrence_email_content = null
    data.recurrence_email_subject = null
    data.delivery_method = null
    data.recurrence_reference_method = null
    delete data.recurrence_mode
    delete data.recurrence_interval_preset
  }

  const submitLock = useRef(false)
  const isSaving = isSubmitting || createMutation.isPending || updateMutation.isPending

  const persistInvoice = async (data: IInvoiceFormValues) => {
    if (submitLock.current) return
    submitLock.current = true
    try {
      if (
        data.recurrence_mode === InvoiceRecurrenceMode.NONE ||
        data?.recurrence_rule?.rule?.frequency === InvoiceRepetitionOptions.NO_SELECTION
      ) {
        clearRecurrenceFields(data)
      } else {
        setRecurrenceRuleEdgeCases(data)
      }

      const created = isEdit
        ? await updateMutation.mutateAsync(data)
        : await createMutation.mutateAsync(data)
      _onSubmit(created)
    } catch {
      submitLock.current = false
      return null
    }
  }

  const onSubmit = handleSubmit(async data => {
    if (isCreditNote && !isEdit) {
      pendingCreditNote.current = data
      setShowCreditNoteConfirm(true)
      return
    }
    await persistInvoice(data)
  })

  const confirmCreditNote = async () => {
    const data = pendingCreditNote.current
    pendingCreditNote.current = null
    setShowCreditNoteConfirm(false)
    if (!data) return
    await persistInvoice(data)
  }

  const copyInvoiceData = useCallback(
    (invoice: IInvoice, useRefNumber: boolean) => {
      const copyValues = getDefaultValues(invoice)

      // Do not copy these values
      delete copyValues['invoicing_date']
      delete copyValues['settlement_date']
      delete copyValues['recurrence_parent_id']
      delete copyValues['next_recurrences']
      delete copyValues['is_active_recurrence_template']

      // Copied rows must not keep source ids — create/update would reject foreign row ids.
      copyValues.rows = (copyValues.rows || []).map(row => {
        const next = { ...row }
        delete next.id
        return next
      })

      if (!useRefNumber) {
        delete copyValues['reference']
      }

      // TODO: add these under the toggle setting in the upcoming PR
      delete copyValues['seller_reference']
      delete copyValues['buyer_reference']

      methods.reset(
        {
          ...getValues(),
          ...copyValues
        },
        { keepDefaultValues: true }
      )
    },
    [methods.reset, getDefaultValues]
  )

  return (
    <FormProvider {...methods}>
      <FormWrapper>
        <form id="invoice-form" onSubmit={onSubmit}>
          <Alert
            isVisible={!canInvoice}
            type="warning"
            title={t('invoicing.form.cannotInvoiceTitle')}
            description={t('invoicing.form.cannotInvoiceDescription')}
          />

          <Alert
            title={t('invoicing.form.missingInvoicingAddressTitle')}
            type="warning"
            isVisible={receiverCantBeInvoiced}
            description={
              <Trans i18nKey={'invoicing.form.missingInvoicingAddress'}>
                <Link to={getContactPageUrl(slug, { id: receiverData?.id })} />
              </Trans>
            }
          />

          <FormGroup>
            <Input
              data-test="invoicing-form-date-input"
              label={t('invoicing.form.invoiceDate')}
              required={true}
              type="date"
              errors={get(errors, 'invoicing_date')}
              {...register('invoicing_date', {
                required: { value: true, message: t('validation.required') },
                onChange: event => {
                  if (
                    hasAccuralBasedEntries !== true ||
                    _isCreditNoteFor ||
                    settlementDateEdited.current
                  )
                    return
                  const nextInvoicingDate = event.target.value
                  if (!nextInvoicingDate) return
                  setValue(
                    'settlement_date',
                    defaultSettlementDate(nextInvoicingDate, invoicingDefaultSettlementDate),
                    { shouldDirty: false, shouldValidate: true }
                  )
                }
              })}
              info={t('invoicing.form.invoiceDateInfo')}
            />

            {hasAccuralBasedEntries === true && (
              <Input
                label={t('invoicing.form.settlementDate')}
                type="date"
                required={true}
                errors={get(errors, 'settlement_date')}
                {...register('settlement_date', {
                  required: { value: true, message: t('validation.required') },
                  onChange: () => {
                    settlementDateEdited.current = true
                  }
                })}
                info={t('invoicing.form.settlementDateInfo')}
              ></Input>
            )}

            {!_isCreditNoteFor && (
              <InvoiceCopyDropdown
                key="invoice-copy-dropdown"
                label={t('document.description')}
                placeholder={t('document.descriptionPlaceholder')}
                info={t('document.descriptionInfo')}
                id="invoice-description"
                slug={slug}
                required={true}
                copyInvoiceData={copyInvoiceData}
              />
            )}

            <Controller
              name="receiver"
              control={control}
              rules={{ required: { value: true, message: t('validation.required') } }}
              render={({ field, fieldState: { error } }) => (
                <ContactSelector
                  noDataMessage={t('contacts.contactSelector.noData')}
                  placeholder={t('contacts.contactSelector.placeholder')}
                  label={t('invoicing.form.receiver')}
                  {...field}
                  errors={error}
                  ref={null}
                  required={true}
                  fetchOnlyInvoicableContacts={true}
                ></ContactSelector>
              )}
            />

            {showGermanBuyerReference && (
              <BuyerReferenceField>
                <Input
                  label={t('invoicing.form.buyerReferenceBt10')}
                  info={t('invoicing.form.buyerReferenceInfoBt10')}
                  placeholder={contactBuyerReference || undefined}
                  style={{ marginBottom: 0 }}
                  errors={get(errors, 'buyer_reference')}
                  {...register('buyer_reference', {
                    validate: value => {
                      if (!value?.trim()) {
                        return true
                      }

                      if (!isValidDeBuyerReferenceFormat(value)) {
                        return t('validation.xrechnung.buyerReferenceInvalid')
                      }

                      return true
                    }
                  })}
                  data-test="invoice-form-buyer-reference"
                />
                {showBuyerReferenceOverrideNote && (
                  <FieldHelperNote>
                    {t('invoicing.form.buyerReferenceOverrideNote', {
                      value: contactBuyerReference
                    })}
                  </FieldHelperNote>
                )}
              </BuyerReferenceField>
            )}

            <InputRow>
              <Input
                label={t('invoicing.form.paymentCondition')}
                required={true}
                type="number"
                info={t('invoicing.form.paymentConditionInfo')}
                errors={get(errors, 'payment_condition_days')}
                {...register('payment_condition_days', {
                  valueAsNumber: true,
                  required: { value: true, message: t('validation.required') }
                })}
              />
              <Input
                disabled
                label={t('invoicing.form.dueDate')}
                type="date"
                info={t('invoicing.form.dueDateInfo')}
                value={dueDate}
              />
            </InputRow>

            <InvoiceFormRecurrenceSection
              invoiceReceiver={receiver}
              toggleAutoOpen={isEdit && defaultValues?.is_active_recurrence_template}
              isCreditNote={!!isCreditNoteFor}
            ></InvoiceFormRecurrenceSection>

            {hasTags && (
              <Controller
                name="tag_ids"
                control={control}
                render={({ field }) => (
                  <TagSelectorWrapper>
                    <TagSelector {...field} />
                  </TagSelectorWrapper>
                )}
              />
            )}
          </FormGroup>

          <TextArea
            label={t('invoicing.form.description')}
            info={t('invoicing.form.descriptionInfo')}
            errors={get(errors, 'description')}
            {...register('description')}
          ></TextArea>

          {/* INTEREST AND REFERENCE */}
          <InvoiceFormMoreSection
            toggleAutoOpen={isEdit && defaultValues?.is_active_recurrence_template}
            isCreditNote={!!isCreditNoteFor}
            hideBuyerReference={isGermanBusiness}
          ></InvoiceFormMoreSection>

          <InvoiceRowsActionBar>
            <SubHeader>{t('invoicing.form.rows.title')}</SubHeader>
            <InvoiceFormRowToggle
              advancedMode={advancedMode}
              setAdvancedMode={setAdvancedModeCallback}
            ></InvoiceFormRowToggle>
          </InvoiceRowsActionBar>

          <InvoiceRows control={control} isAdvanced={advancedMode} />

          <SubHeader>
            <span>{t('invoicing.form.attachments')}</span>
            <TextTooltip tooltip={t('invoicing.form.attachmentsInfo')}>
              <FaQuestionCircle />
            </TextTooltip>
          </SubHeader>
          <Controller
            name="attachments"
            control={control}
            render={({ field: { value, onChange } }) => (
              <AttachmentSelector
                value={value}
                onChange={onChange}
                showRemoveInsteadOfViewLink={true}
              />
            )}
          />
        </form>

        <InvoiceFooterWrapper>
          <Alert
            isVisible={Boolean((errors as any)?.general?.message)}
            type="error"
            description={(errors as any)?.general?.message}
          />

          <InvoiceFooterContent>
            <InvoiceFooterCol>
              <InvoiceRowColHeader>{t('invoicing.form.totals.net')}</InvoiceRowColHeader>
              <InvoiceRowColValue>{<MonetaryAmount value={totals.net} />}</InvoiceRowColValue>
            </InvoiceFooterCol>

            <InvoiceFooterCol>
              <InvoiceRowColHeader>{t('invoicing.form.totals.vat')}</InvoiceRowColHeader>
              <InvoiceRowColValue>{<MonetaryAmount value={totals.vat} />}</InvoiceRowColValue>
            </InvoiceFooterCol>

            <InvoiceFooterCol>
              <InvoiceRowColHeader>{t('invoicing.form.totals.sum')}</InvoiceRowColHeader>
              <InvoiceRowColValue>{<MonetaryAmount value={totals.tot} />}</InvoiceRowColValue>
            </InvoiceFooterCol>
          </InvoiceFooterContent>

          <FooterButtonContainer>
            <Button type="button" data-test="cancel-invoicee-button" onClick={() => _onSubmit()}>
              {t('general.cancel')}
            </Button>
            <Button type="button" icon={<FaUndo />} onClick={() => resetForm()} tabIndex={-1}>
              {t('general.startOver')}
            </Button>
            <div style={{ flex: 1 }} />
            <Button
              type="submit"
              data-test="submit-invoice-button"
              icon={<FaSave />}
              form="invoice-form"
              intent="success"
              showSpinner={isSaving}
              disabled={!canInvoice || isSaving || receiverCantBeInvoiced}
            >
              {t('invoicing.form.save')}
            </Button>
          </FooterButtonContainer>
        </InvoiceFooterWrapper>
        <Prompt
          isVisible={showCreditNoteConfirm}
          title={t('general.areYouSure')}
          description={t('invoicing.form.creditNoteConfirm')}
          onClose={() => {
            pendingCreditNote.current = null
            setShowCreditNoteConfirm(false)
          }}
          buttons={[
            {
              text: t('general.cancel'),
              action: () => {
                pendingCreditNote.current = null
                setShowCreditNoteConfirm(false)
              }
            },
            {
              text: t('invoicing.details.refund'),
              intent: 'success',
              action: () => confirmCreditNote(),
              showSpinner: createMutation.isPending,
              disabled: createMutation.isPending
            }
          ]}
        />
      </FormWrapper>
    </FormProvider>
  )
}

const InputRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const TagSelectorWrapper = styled.div`
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`

const FormGroup = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.lg}rem;
`

const SubHeader = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  margin-bottom: ${({ theme }) => theme.spacing.sm}rem;

  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`

const FormWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;

  form {
    overflow-y: auto;
    overflow-x: hidden;
    padding-bottom: 1rem;
    flex: 1;
  }
`

const InvoiceFooterWrapper = styled.div`
  padding-top: ${({ theme }) => theme.spacing.lg}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const InvoiceFooterContent = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const InvoiceFooterCol = styled.div``

const InvoiceRowColHeader = styled.div`
  font-weight: bold;
  white-space: nowrap;
  color: ${({ theme }) => theme.colors.metalGray};
`

const InvoiceRowColValue = styled.div`
  font-size: ${({ theme }) => theme.fontSize.md}rem;
  font-variant-numeric: tabular-nums;
`

const FooterButtonContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin-top: ${({ theme }) => theme.spacing.md}rem;
  gap: ${({ theme }) => theme.spacing.xs}rem;
`
const InvoiceRowsActionBar = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;

  svg {
    color: ${({ theme }) => theme.colors.metalGray};
  }
`

const BuyerReferenceField = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs}rem;
  margin-bottom: ${({ theme }) => theme.spacing.xs}rem;
`

const FieldHelperNote = styled.p`
  margin: 0;
  padding: 0 ${({ theme }) => theme.spacing.xs}rem;
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  color: ${({ theme }) => theme.colors.metalGray};
  line-height: 1.4;
`
